import {
  CardStatus,
  CardType,
  ICardResponse
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  useModalContext,
  useWSSnackbar,
  WSButton,
  WSDivider,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useState } from "react";
import { Route, useHistory } from "react-router-dom";
import { useCreateCard } from "../../../../query/payments/mutations";
import { useCardsQuery } from "../../../../query/payments/queries";
import { getBankCards } from "../../../../query/payments/selectors";
import { WSQueries } from "../../../../query/WSQuery";
import {
  BankCard,
  CardSuccessPhysical,
  JustCreatedPhysicalCard,
  NotActivatedPhysicalCard,
  PhysicalCardPlaceholder,
  VirtualCardPlaceholder
} from "../BankCard/BankCard";
import { BankCardDetails } from "./BankCardDetails";
import { DEBIT_CARD_TC_MODAL, DebitCardTCModal } from "./DebitCardTCModal";
import { useActivities } from "../../../../query/users/queries";
import { useUserId } from "../../../../query/hooks/helpers";
import { openInNewTab } from "../../../../utils/openInNewTab";
import {
  CREATE_PHYSICAL_DEBIT_CARD_MODAL,
  CreatePhysicalCardModal
} from "./CreatePhysicalCard";
import { ACTIVATE_CARD_MODAL, ActivateCardModal } from "./ActivateCard";
import { AddPinCardModal } from "./AddPin";
import { useOTPProtector } from "../BankCard/BankCardOTPModal";

type Props = {
  basePath: string;
  hideBanner?: boolean;
  isMigraitonInProgress?: boolean;
} & WSElementProps;

export const BankCardsList: React.FC<Props> = ({
  basePath,
  isMigraitonInProgress,
  hideBanner,
  ...elementProps
}) => {
  const { openModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();
  const cardsQuery = useCardsQuery();
  const history = useHistory();
  const [createCard, createCardMeta] = useCreateCard();
  const userId = useUserId();
  const qUserActivity = useActivities(userId);
  const [showDeliveringStatus, setShowDeliveringStatus] = useState(false);
  const otpProtector = useOTPProtector();

  const onAddCard = async () => {
    await createCard(
      {},
      {
        onSuccess: () => {
          openSnackbar({
            duration: 5000,
            type: "success",
            message: "Card created successfully!"
          });
        },
        onError: () => {
          openSnackbar({
            duration: 5000,
            type: "warning",
            message: "Failed to create a card!"
          });
        }
      }
    );
  };

  const onView = useCallback((card: ICardResponse) => {
    history.push({
      pathname: `${basePath}/${card.cardId}`,
      state: {
        backPath: basePath
      }
    });
  }, []);

  const [hideCardPromo, setHideCardPromo] = useState(
    !!localStorage.getItem("hideWalletOrderCardPromo")
  );

  return (
    <WSElement {...elementProps}>
      <Route path={`${basePath}/:cardId`} component={BankCardDetails} />
      <DebitCardTCModal />
      <CreatePhysicalCardModal />
      <ActivateCardModal />
      <AddPinCardModal />
      <WSQueries queries={{ cardsQuery, qUserActivity }}>
        {({ cardsQuery: { data: cards }, qUserActivity }) => {
          const virtualCards = getBankCards(cards).filter(
            card =>
              card.type === CardType.businessVirtualDebitCard ||
              card.type === CardType.individualVirtualDebitCard
          );

          const physicalCards = getBankCards(cards).filter(
            card =>
              (card.type === CardType.businessDebitCard ||
                card.type === CardType.individualDebitCard) &&
              (card.status === CardStatus.Frozen ||
                card.status === CardStatus.Active ||
                card.status === CardStatus.Inactive)
          );

          const hasPhysicalActiveCard = cards.some(
            c =>
              c.type === CardType.businessDebitCard ||
              c.type === CardType.individualDebitCard
          );

          const onAddVirtualCard = async () => {
            if (qUserActivity.data.events.debitCardTCAcceptedAt) {
              await onAddCard();
            } else {
              openModal(DEBIT_CARD_TC_MODAL, {
                callback: onAddCard
              });
            }
          };

          const onAddPhysicalCard = async () => {
            if (qUserActivity.data.events.debitCardTCAcceptedAt) {
              openModal(CREATE_PHYSICAL_DEBIT_CARD_MODAL, {
                isReplace: false,
                onSuccess() {
                  setShowDeliveringStatus(true);
                  setTimeout(() => {
                    setShowDeliveringStatus(false);
                  }, 5000);
                }
              });
            } else {
              openModal(DEBIT_CARD_TC_MODAL, {
                callback: () => {
                  openModal(CREATE_PHYSICAL_DEBIT_CARD_MODAL, {
                    isReplace: false,
                    onSuccess() {
                      setShowDeliveringStatus(true);
                      setTimeout(() => {
                        setShowDeliveringStatus(false);
                      }, 5000);
                    }
                  });
                }
              });
            }
          };

          return (
            <>
              {hideBanner || hideCardPromo || hasPhysicalActiveCard ? null : (
                <WSPanel
                  p="NONE"
                  my="XL"
                  onClose={() => {
                    setHideCardPromo(true);
                    window.localStorage.setItem(
                      "hideWalletOrderCardPromo",
                      "true"
                    );
                  }}
                >
                  <WSElement p="M">
                    <WSText.Heading5>
                      Want a free Wingspan Wallet debit card?
                    </WSText.Heading5>
                    <WSText mt="XL">
                      Pay for business expenses with a physical debit card
                      connected to your Wingspan Wallet.
                    </WSText>

                    <WSFlexBox.CenterY mt="XL">
                      <WSIcon
                        block
                        color="red400"
                        mr="M"
                        name="card"
                        size="S"
                      />
                      <WSText.ParagraphSm color="gray500">
                        Supported at over 40,000 ATMs
                      </WSText.ParagraphSm>
                    </WSFlexBox.CenterY>
                    <WSFlexBox.CenterY mt="XL">
                      <WSIcon
                        block
                        color="red400"
                        mr="M"
                        name="dollar-circle"
                        size="S"
                      />
                      <WSText.ParagraphSm color="gray500">
                        No fees or monthly cost
                      </WSText.ParagraphSm>
                    </WSFlexBox.CenterY>
                    <WSFlexBox.CenterY mt="XL">
                      <WSIcon
                        block
                        color="red400"
                        mr="M"
                        name="calculator"
                        size="S"
                      />
                      <WSText.ParagraphSm color="gray500">
                        Automatic tax deductions
                      </WSText.ParagraphSm>
                    </WSFlexBox.CenterY>
                    <WSFlexBox.CenterY mt="XL">
                      <WSIcon
                        block
                        color="red400"
                        mr="M"
                        name="transfer"
                        size="S"
                      />
                      <WSText.ParagraphSm color="gray500">
                        Free ACH transfers
                      </WSText.ParagraphSm>
                    </WSFlexBox.CenterY>
                    <WSFlexBox.CenterY mt="XL">
                      <WSIcon
                        block
                        color="red400"
                        mr="M"
                        name="bank"
                        size="S"
                      />
                      <WSText.ParagraphSm color="gray500">
                        $0 account minimum
                      </WSText.ParagraphSm>
                    </WSFlexBox.CenterY>
                  </WSElement>
                  <WSDivider />
                  <WSFlexBox.CenterY px="XL" py="M">
                    <WSButton.Link
                      onClick={onAddPhysicalCard}
                      disabled={isMigraitonInProgress}
                    >
                      Order free card
                    </WSButton.Link>
                    <WSIcon
                      block
                      ml="M"
                      color="blue500"
                      size="XS"
                      name="open-tab"
                    />
                  </WSFlexBox.CenterY>
                </WSPanel>
              )}

              <WSText.Heading4 my="2XL">Virtual debit card</WSText.Heading4>
              {virtualCards.length === 0 ? (
                <VirtualCardPlaceholder
                  onClickLink={onAddVirtualCard}
                  data-testid="createCard"
                />
              ) : (
                virtualCards.map(card => (
                  <BankCard
                    key={card.cardId}
                    card={card}
                    onDetailsClick={() => {
                      onView(card);
                    }}
                  />
                ))
              )}

              <WSFlexBox.CenterY my="2XL" justify="space-between">
                <WSText.Heading4>Physical debit card</WSText.Heading4>
                {!hideBanner && (
                  <WSButton.Link
                    onClick={() =>
                      openInNewTab(
                        "https://www.allpointnetwork.com/locator.html"
                      )
                    }
                  >
                    Search for ATMs →
                  </WSButton.Link>
                )}
              </WSFlexBox.CenterY>

              {physicalCards.length === 0 ? (
                <PhysicalCardPlaceholder
                  onClickLink={onAddPhysicalCard}
                  data-testid="createCard"
                  disabled={isMigraitonInProgress}
                />
              ) : (
                physicalCards.map(card =>
                  card.status === CardStatus.Active ||
                  card.status === CardStatus.Frozen ? (
                    <CardSuccessPhysical
                      isLocked={card.status === CardStatus.Frozen}
                      onClick={() => onView(card)}
                      last4={card.last4Digits}
                    />
                  ) : showDeliveringStatus ? (
                    <JustCreatedPhysicalCard />
                  ) : (
                    <NotActivatedPhysicalCard
                      onClick={() => {
                        onView(card);
                      }}
                      onClickLink={() => {
                        otpProtector(card, () => {
                          openModal(ACTIVATE_CARD_MODAL, {
                            cardId: card.cardId
                          });
                        });
                      }}
                    />
                  )
                )
              )}
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
